'use client';

import React, { createContext, useCallback, useContext, useState, useMemo } from 'react';

interface ChatContextValue {
  chatOpen: boolean;
  setChatOpen: (open: boolean) => void;
  presetPrompt: string | null;
  openChatWithPrompt: (prompt: string) => void;
  clearPresetPrompt: () => void;
  initialAssistantMessage: string | null;
  openChatWithGreeting: (greeting: string) => void;
  clearInitialAssistantMessage: () => void;
}

const ChatContext = createContext<ChatContextValue>({
  chatOpen: false,
  setChatOpen: () => {},
  presetPrompt: null,
  openChatWithPrompt: () => {},
  clearPresetPrompt: () => {},
  initialAssistantMessage: null,
  openChatWithGreeting: () => {},
  clearInitialAssistantMessage: () => {},
});

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [presetPrompt, setPresetPrompt] = useState<string | null>(null);
  const [initialAssistantMessage, setInitialAssistantMessage] = useState<string | null>(null);
  const openChatWithPrompt = useCallback((prompt: string) => {
    setPresetPrompt(prompt);
    setChatOpen(true);
  }, []);
  const clearPresetPrompt = useCallback(() => setPresetPrompt(null), []);
  const openChatWithGreeting = useCallback((greeting: string) => {
    setInitialAssistantMessage(greeting);
    setChatOpen(true);
  }, []);
  const clearInitialAssistantMessage = useCallback(() => setInitialAssistantMessage(null), []);
  const value = useMemo(
    () => ({ chatOpen, setChatOpen, presetPrompt, openChatWithPrompt, clearPresetPrompt, initialAssistantMessage, openChatWithGreeting, clearInitialAssistantMessage }),
    [chatOpen, presetPrompt, openChatWithPrompt, clearPresetPrompt, initialAssistantMessage, openChatWithGreeting, clearInitialAssistantMessage]
  );
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChatOpen() {
  return useContext(ChatContext);
}
